import { useEffect } from 'react';
import styled from 'styled-components';
import {
  cssColorValue,
  cssColorVar,
  ProgressDots,
  TextLink,
  spacer16,
} from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import { useGetCatalogLatestDataAvailabilityDate } from '@hooks/useGetCatalogLatestDataAvailabilityDate';
import { EVENT_ACTION, EVENT_CATEGORY } from '@lib/ga/types';
import { GA_PRODUCT_CATALOG } from '@helpers/constants';

type DataFreshnessDisplayProps = {
  exportTypeIndex: number;
};

const DataFreshnessComponentContainer = styled.div`
  min-width: 440px;
  min-height: 54px;
  padding: ${spacer16};
  display: flex;
  align-items: center;
  background-color: ${cssColorValue('backgroundHighlight')};
`;

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: row;
  min-width: 100%;
  justify-content: center;
`;

const ResultsLabel = styled.div`
  color: var(${cssColorVar('textSubdued')});
`;

const ProgressDotsContainer = styled.div`
  align-items: center;
  min-width: 100%;
  display: flex;
  justify-content: center;
`;

const StyledTextLink = styled(TextLink)`
  cursor: pointer;
`;

const ErrorContainer = styled.div`
  min-width: 100%;
  justify-content: center;
  display: flex;
`;

export const DataFreshnessDisplay = ({
  exportTypeIndex,
}: DataFreshnessDisplayProps) => {
  const t = useT();

  const getExportType = (currentExportTypeIndex: number) => {
    if (currentExportTypeIndex === 1) {
      return 'TRACK';
    } else if (currentExportTypeIndex === 2) {
      return 'VIDEO';
    }
    return 'ALBUM';
  };

  const {
    data: latestDataAvailabilityDateData,
    error,
    mutate,
    actions: { getData },
    isLoading,
    isValidating,
  } = useGetCatalogLatestDataAvailabilityDate({
    exportType: getExportType(exportTypeIndex),
  });

  useEffect(() => {
    getData({ exportType: getExportType(exportTypeIndex) });
  }, [exportTypeIndex, getData]);

  const handleRetry = () => {
    sendEvent({
      eventCategory: EVENT_CATEGORY.CATALOG_EXPORTS_DATA_FRESHNESS,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: `Clicked the retry button`,
      product: GA_PRODUCT_CATALOG,
    });
    mutate();
  };

  const renderDateOrRetryButton = () => {
    if (error) {
      return (
        <ErrorContainer>
          <StyledTextLink
            semanticColor="textNegative"
            tabIndex={0}
            standalone
            onClick={handleRetry}
          >
            {t(
              'atlas.actions.error_loading_try_again',
              'Error loading catalog availability data, try again',
              'Text button to try to fetch data again if there was an error loading',
            )}
          </StyledTextLink>
        </ErrorContainer>
      );
    }

    const mostRecentDate = `${latestDataAvailabilityDateData.mostRecentDataAvailableDate} 00:00:00 UTC`;
    return (
      <ResultsLabel>
        {t(
          'atlas.labels.catalog_data_available_as_of',
          'Catalog data available as of {date}',
          'Text that indicates the date the catalog export will be based on',
          { date: mostRecentDate },
        )}
      </ResultsLabel>
    );
  };

  return (
    <DataFreshnessComponentContainer>
      <ResultsContainer>
        {isLoading || isValidating || !latestDataAvailabilityDateData ? (
          <ProgressDotsContainer>
            <ProgressDots size="small" />
          </ProgressDotsContainer>
        ) : (
          renderDateOrRetryButton()
        )}
      </ResultsContainer>
    </DataFreshnessComponentContainer>
  );
};
